import { UserRight } from '../core/authentication/login/user.model';
import { FormName, getFormNameLabel } from '../core/layout/form/form-name';
import { URLS } from '../core/constant/url-schema.constant';

export interface SettingTab {
  title: string;
  path: string;
  rights: UserRight[];
}

export interface SettingCard {
  name: string;
  icon: string;
  path: string;
  tabs: SettingTab[];
}

export interface SettingSection {
  label: string;
  accent: string;
  cards: SettingCard[];
}

export const SETTING_SECTIONS: SettingSection[] = [
  {
    label: $localize`Dashboards`,
    accent: 'amber',
    cards: [
      {
        name: $localize`Dashboards`,
        icon: 'dashboard',
        path: '/' + URLS.SETTING_DASHBOARD,
        tabs: [
          {
            title: getFormNameLabel(FormName.dashboard, true, true),
            path: '/' + URLS.SETTING_DASHBOARD,
            rights: [],
          },
          {
            title: getFormNameLabel(FormName.schedule_report, true, true),
            path: '/' + URLS.SETTING_SCHEDULE_REPORT,
            rights: [UserRight.DASHBOARDS],
          },
          {
            title: getFormNameLabel(FormName.playlist, true, true),
            path: '/' + URLS.SETTINGS_DASHBOARD_PLAYLISTS,
            rights: [],
          },
        ],
      },
      {
        name: $localize`DashViews`,
        icon: 'map',
        path: '/' + URLS.DASHVIEW,
        tabs: [
          { title: $localize`DashViews`, path: '/' + URLS.DASHVIEW, rights: [UserRight.DASHVIEW] },
          {
            title: $localize`Backgrounds`,
            path: '/' + URLS.BACKGROUND,
            rights: [UserRight.DASHVIEW],
          },
        ],
      },
    ],
  },
  {
    label: $localize`Perimeter`,
    accent: 'blue',
    cards: [
      {
        name: $localize`Measures`,
        icon: 'table_chart',
        path: '/' + URLS.SETTINGS_MEASURE,
        tabs: [
          { title: $localize`Measures`, path: '/' + URLS.MEASURE, rights: [UserRight.MEASURES] },
          {
            title: $localize`Matrix`,
            path: '/' + URLS.MEASURE_MATRIX,
            rights: [UserRight.MEASURES],
          },
          { title: $localize`Datasets`, path: '/' + URLS.DATASET, rights: [UserRight.DATASET] },
        ],
      },
      {
        name: $localize`Hosts`,
        icon: 'computer',
        path: '/' + URLS.HOST,
        tabs: [
          { title: $localize`Hosts`, path: '/' + URLS.HOST, rights: [UserRight.HOSTS] },
          { title: $localize`Sites`, path: '/' + URLS.SITE, rights: [UserRight.HOSTS] },
          { title: $localize`Cities`, path: '/' + URLS.CITY, rights: [UserRight.HOSTS] },
          { title: $localize`Perimeters`, path: '/' + URLS.PERIMETER, rights: [UserRight.HOSTS] },
          { title: $localize`Maintainers`, path: '/' + URLS.MAINTAINER, rights: [UserRight.HOSTS] },
          { title: $localize`Owners`, path: '/' + URLS.OWNER, rights: [UserRight.HOSTS] },
          { title: $localize`Providers`, path: '/' + URLS.PROVIDER, rights: [UserRight.HOSTS] },
          { title: $localize`Roles`, path: '/' + URLS.HOST_ROLE, rights: [UserRight.HOSTS] },
          { title: $localize`Sites types`, path: '/' + URLS.SITETYPE, rights: [UserRight.HOSTS] },
          {
            title: $localize`Network types`,
            path: '/' + URLS.NETWORK_TYPE,
            rights: [UserRight.HOSTS],
          },
          { title: $localize`Interfaces`, path: '/' + URLS.INTERFACE, rights: [UserRight.HOSTS] },
        ],
      },
      {
        name: $localize`Scripting`,
        icon: 'code',
        path: '/' + URLS.SCRIPTING,
        tabs: [
          {
            title: $localize`Scripts`,
            path: '/' + URLS.SCRIPT,
            rights: [UserRight.MEASURES, UserRight.MEASURES_SCRIPT],
          },
          {
            title: $localize`My scripts`,
            path: '/' + URLS.MY_SCRIPTS,
            rights: [UserRight.MEASURES, UserRight.MEASURES_MY_SCRIPT],
          },
          { title: $localize`Scenarios`, path: '/' + URLS.SCENARIO, rights: [UserRight.MEASURES] },
          {
            title: $localize`Applications`,
            path: '/' + URLS.APPLICATION,
            rights: [UserRight.MEASURES],
          },
          {
            title: $localize`Shared action groups`,
            path: '/' + URLS.SHARED_ACTION_GROUPS,
            rights: [UserRight.MEASURES_MY_SCRIPT],
          },
          {
            title: $localize`Credentials`,
            path: '/' + URLS.CREDENTIALS,
            rights: [UserRight.MEASURES_MY_SCRIPT],
          },
          {
            title: $localize`Images`,
            path: '/' + URLS.IMAGE,
            rights: [UserRight.MEASURES_MY_SCRIPT],
          },
        ],
      },
      {
        name: $localize`Traffic`,
        icon: 'traffic',
        path: '/' + URLS.PROBE,
        tabs: [
          { title: $localize`Probes`, path: '/' + URLS.PROBE, rights: [UserRight.TRAFFIC] },
          {
            title: $localize`Flow reports`,
            path: '/' + URLS.TRAFFICFLOWREPORTS,
            rights: [UserRight.TRAFFIC],
          },
          {
            title: $localize`Flow pools`,
            path: '/' + URLS.FLOW_POOLS,
            rights: [UserRight.TRAFFIC],
          },
          {
            title: $localize`IP Services`,
            path: '/' + URLS.IPSERVICE,
            rights: [UserRight.TRAFFIC],
          },
          { title: $localize`Subnets`, path: '/' + URLS.SUBNET, rights: [UserRight.TRAFFIC] },
          {
            title: $localize`Types of service`,
            path: '/' + URLS.TYPEOFSERVICE,
            rights: [UserRight.TRAFFIC],
          },
        ],
      },
    ],
  },
  {
    label: $localize`Events & alerts`,
    accent: 'coral',
    cards: [
      {
        name: $localize`SmartAlerts`,
        icon: 'notifications',
        path: '/' + URLS.SMARTALERT,
        tabs: [
          {
            title: $localize`SmartAlerts`,
            path: '/' + URLS.SMARTALERT,
            rights: [UserRight.SMARTALERTS],
          },
          { title: $localize`Events`, path: '/' + URLS.EVENT, rights: [UserRight.SMARTALERTS] },
          {
            title: $localize`Notifications Templates`,
            path: '/' + URLS.TEMPLATE,
            rights: [UserRight.SMARTALERTS_NOTIFICATIONS],
          },
        ],
      },
      {
        name: $localize`Contacts`,
        icon: 'contact_page',
        path: '/' + URLS.CONTACTS,
        tabs: [
          {
            title: $localize`Contacts`,
            path: '/' + URLS.CONTACT,
            rights: [UserRight.USERS, UserRight.USERS_CONTACT],
          },
          {
            title: $localize`Contact Groups`,
            path: '/' + URLS.CONTACTGROUP,
            rights: [UserRight.USERS, UserRight.USERS_CONTACT],
          },
        ],
      },
      {
        name: $localize`Periods`,
        icon: 'calendar_month',
        path: '/' + URLS.MAINTENANCE,
        tabs: [
          {
            title: $localize`Maintenances`,
            path: '/' + URLS.MAINTENANCE,
            rights: [UserRight.PERIODS_MAINTENANCES],
          },
          {
            title: $localize`Analysis Ranges`,
            path: '/' + URLS.ANALYSIS_RANGE,
            rights: [UserRight.PERIODS_ANALYSIS],
          },
        ],
      },
    ],
  },
  {
    label: $localize`Administration`,
    accent: 'gray',
    cards: [
      {
        name: $localize`Users and roles`,
        icon: 'groups',
        path: '/' + URLS.USER,
        tabs: [
          { title: $localize`Users`, path: '/' + URLS.USER, rights: [UserRight.USERS] },
          {
            title: $localize`Functionality groups`,
            path: '/' + URLS.FUNCTIONALITYGROUP,
            rights: [UserRight.USERS],
          },
          {
            title: $localize`Views groups`,
            path: '/' + URLS.VIEW_GROUP,
            rights: [UserRight.USERS],
          },
          { title: $localize`Views`, path: '/' + URLS.VIEW, rights: [UserRight.USERS] },
          { title: $localize`API keys`, path: '/' + URLS.API_KEY, rights: [UserRight.SUPER_USER] },
        ],
      },
      {
        name: $localize`Advanced`,
        icon: 'tune',
        path: '/' + URLS.ADVANCED,
        tabs: [
          {
            title: $localize`Customer rules`,
            path: '/' + URLS.CUSTOMERRULE,
            rights: [UserRight.ADVANCED],
          },
          {
            title: $localize`Inventory rules`,
            path: '/' + URLS.INVENTORY_RULES,
            rights: [UserRight.ADVANCED],
          },
          {
            title: $localize`Measurements types`,
            path: '/' + URLS.MEASURETYPE,
            rights: [UserRight.ADVANCED],
          },
          {
            title: $localize`Results types`,
            path: '/' + URLS.RESULTTYPE,
            rights: [UserRight.ADVANCED],
          },
          { title: $localize`Labels`, path: '/' + URLS.LABEL, rights: [UserRight.ADVANCED] },
          {
            title: $localize`SmartReports`,
            path: '/' + URLS.REPORT,
            rights: [UserRight.ADVANCED, UserRight.REPORTS],
          },
          {
            title: $localize`Parenthood`,
            path: '/' + URLS.PARENTHOOD,
            rights: [UserRight.SUPER_USER],
          },
          {
            title: $localize`Setup stack`,
            path: '/' + URLS.SETUP_STACK,
            rights: [UserRight.ADVANCED],
          },
          { title: $localize`Status`, path: '/status', rights: [UserRight.SUPER_USER] },
        ],
      },
    ],
  },
];

export const SETTING_CARDS: SettingCard[] = SETTING_SECTIONS.flatMap(section => section.cards);

export function cardRights(card: SettingCard): UserRight[] {
  return card.tabs.some(tab => tab.rights.length === 0)
    ? []
    : [...new Set(card.tabs.flatMap(tab => tab.rights))];
}
